"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/************************************** THIRD-PARTY MODULES ***************************************/
var shared_1 = require("mad-utils/lib/shared");
var cvcAntigens = require('@canimmunize/cvc/release/gen/canimmunize/aad.json');
var cvcDiseases = require('@canimmunize/cvc/release/gen/canimmunize/diseases.json');
var cvcGenerics = require('@canimmunize/cvc/release/gen/canimmunize/generics.json');
var cvcTradenames = require('@canimmunize/cvc/release/gen/canimmunize/tradenames.json');
/**************************************** PROJECT MODULES *****************************************/
var javelin_exports_1 = require("../javelin-exports");
var Type_1 = require("../../.constants/Type");
var SyncType_1 = require("../../.constants/SyncType");
var regions_1 = require("./hydrators/regions");
var store_1 = require("./store/store");
var javelin_data_util_1 = require("./util/javelin-data-util");
/************************************** IMPORT HYDRATED DATA **************************************/
var vaccines_1 = require("./hydrators/vaccines");
var diseases_1 = require("./hydrators/diseases");
var disease_concepts_1 = require("./hydrators/disease-concepts");
var generic_vaccine_concepts_1 = require("./hydrators/generic-vaccine-concepts");
var trade_name_concepts_1 = require("./hydrators/trade-name-concepts");
var generator_collections_1 = require("./hydrators/generator-collections");
var dose_generators_1 = require("./hydrators/dose-generators");
var schedules_1 = require("./hydrators/schedules");
var phu_configs_1 = require("./hydrators/phu-configs");
// Config data
var configData = require('./sources/config-main.json');
/******************************************** LOGGING *********************************************/
var util_1 = require("util");
var shared_2 = require("mad-logs/lib/shared");
var log = shared_2.logFactory('javelin-data.ts', shared_2.Styles.aquarium, util_1.inspect);
/********************************************* EXPORT *********************************************/
/**
 * Interface used by the [DataManager]
 *
 * @author Andrew Faulkner & Julien Guerinet
 * @since 1.2.0
 */
var JavelinData = /** @class */ (function () {
    /*** INITIALIZATION ***/
    /**
     * Build JavelinData manager object, including hydrating the store for the 1st time
     * Runs *before* syncing occurs
     */
    function JavelinData(rawContentData, rawDiseaseData, rawVaccineData, rawPHUConfigData) {
        var _this = this;
        /*** JAVELIN SETTINGS ***/
        /**
         * Returns true if a schedule is required for a [Record], false otherwise
         */
        this.isScheduleRequired = true;
        /**
         * Returns true if a region is required for a [Record], false otherwise
         */
        this.isRegionRequired = true;
        /**
         * Returns list of regions by their region code
         */
        this.regions = regions_1.regionArray;
        /*** GETTERS ***/
        /**
         * Returns the list of influenza vaccine concept Ids
         */
        this.influenzaVaccineConceptIds = configData.influenzaVaccineConceptIds;
        /**
         * Returns the list of tetanus disease concept Ids
         */
        this.tetanusDiseaseConceptIds = configData.tetanusDiseaseConceptIds;
        /**
         * Returns array of main disease concept Ids {string[]}
         */
        this.mainDiseaseConceptIds = configData.mainDiseaseConceptIds;
        /*** QUERYING ***/
        /**
         * All queries are in this form:
         *     SELECT * FROM `SomeClass` WHERE A & B & C & ... & N
         *
         *     Where clauses:
         *         val1  ==  val2
         *
         * Get 1st matching object in store, based on given SQL query (parsed in client) & class type
         *
         * @param aClass Object class that we are looking for
         * @param query  SQL query
         * @param <T>    Object type
         * @return Corresponding object, null if none
         */
        this.querySingle = function (aClass, query) {
            return javelin_data_util_1.queryData('single', query, _this.getModelData);
        };
        /**
         * Get array of matching objects (in the store) based on given SQL query and class type
         * @param aClass Object class that we are looking for
         * @param query  SQL query
         * @param <T>    Object type
         * @return Corresponding list of objects, an empty list if none
         */
        this.queryList = function (aClass, query) {
            return javelin_data_util_1.queryData('list', query, _this.getModelData);
        };
        /**
         * TODO Ensure operateList works (I suspect it doesn't, but also that it may not need to)
         * Allows us to perform an operation on all of the objects found by the given query
         *
         * @param aClass    Object class we are looking for
         * @param query     SQL query
         * @param operation Operation to run on the objects
         * @param <T>       Object type
         */
        this.operateList = function (aClass, query, operation) {
            var result = _this.queryList(aClass, query);
            try {
                // TODO deal with not using mapped result
                result = result.map(operation);
            }
            catch (error) {
                // prettier-ignore
                log.error("[operateList] :: mapping result:", result, "\n vs query: " + query + "\n failed w error:", error, "\nOperation attempted on result (operation):", operation, "\nProvided aClass:", aClass);
                // Don't rethrow: this can get triggered for many hard-to-foresee
                // reasons, and it shouldn't crash the entire app - it can usually
                // recover (Also, rethrowing here makes debugging it harder)
            }
            return null;
        };
        /**
         * TODO What do I do with JavelinData#deleteList?
         *
         * Deletes all of the objects of the given class that fit the given query
         *
         * @param aClass Object class that we are looking for
         * @param query  SQL Query
         * @param <T>    Object type
         */
        this.deleteList = function (aClass, query) {
            return null;
        };
        /**
         * Checks if there is info that exists for the given SQL query
         *
         * @param aClass Object class that we are looking for
         * @param query  SQL Query
         * @param <T>    Object type
         * @return True if there is info that exists, false otherwise
         */
        this.exists = function (aClass, query) {
            var queryResult = _this.queryList(aClass, query);
            if (Array.isArray(queryResult) && queryResult.length === 0)
                return false;
            return !!queryResult;
        };
        /*** DATA DISPATCH ***/
        /**
         * Return data corresponding to the correct data type
         */
        this.getModelData = function (modelName) {
            log.verbose("getModelData :: modelName: " + modelName);
            // prettier-ignore
            switch (modelName) {
                // Banners aren't accessible from JavelinData (they come from the REST API)
                case Type_1.Type.BANNER:
                    log.warn("getModelData->Type.BANNER: JVLNS doesn't store banners");
                    break;
                // [Fixes "multiple dose setting" issue]
                // Attempting to eliminate doses store & instead use dose values stored on records
                case Type_1.Type.DOSE: return shared_1.flatten(_this.store.records.map(function (record) { return record.doses; }));
                case Type_1.Type.CITY: return _this.store.cities;
                case Type_1.Type.DISEASE: return _this.store.diseases;
                case Type_1.Type.DISEASE_CONCEPT: return _this.store.diseaseConcepts;
                case Type_1.Type.GENERIC_CONCEPT: return _this.store.genericConcepts;
                case Type_1.Type.TRADE_NAME_CONCEPT: return _this.store.tradeNameConcepts;
                case Type_1.Type.SCHEDULE: return _this.store.schedules;
                case Type_1.Type.VACCINE: return _this.store.vaccines;
                case Type_1.Type.RECORD: return _this.store.records;
                case Type_1.Type.COLLECTION: return _this.store.generatorCollections;
                case Type_1.Type.GENERATOR: return _this.store.doseGenerators;
                case Type_1.Type.PHU_INTEGRATION: return _this.store.phuIntegrations;
                case Type_1.Type.PHU_CONFIG: return _this.store.phuConfigs;
                // Sync types
                case SyncType_1.SyncType.Simple.TOKEN: return _this.store.token;
                case SyncType_1.SyncType.Simple.PASSWORD: return _this.store.password;
                case SyncType_1.SyncType.Simple.LOCK: return _this.store.lock;
                case SyncType_1.SyncType.ACCOUNT: return _this.store.account;
                case SyncType_1.SyncType.TRANSACTION: return _this.store.transactions;
                default: throw new Error("getModelData: Unknown model type - modelName: " + modelName);
            }
        };
        rawContentData.diseaseConcepts = cvcDiseases.map(cvcDiseaseConceptMap);
        rawContentData.genericConcepts = cvcGenerics.map(cvcGenericConceptMap);
        rawContentData.tradeNameConcepts = cvcTradenames.map(cvcTradenameConceptsMap);
        this.store = store_1.store.hydrate({
            diseases: diseases_1.diseases(rawDiseaseData),
            diseaseConcepts: disease_concepts_1.diseaseConcepts(rawContentData),
            doseGenerators: dose_generators_1.doseGenerators(rawContentData),
            generatorCollections: generator_collections_1.generatorCollections(rawContentData),
            schedules: schedules_1.schedules(rawContentData),
            tradeNameConcepts: trade_name_concepts_1.tradeNameConcepts(rawContentData),
            vaccines: vaccines_1.vaccines(rawVaccineData),
            genericConcepts: generic_vaccine_concepts_1.genericVaccineConcepts(rawContentData),
            doses: [],
            records: [],
            phuIntegrations: [],
            phuConfigs: phu_configs_1.phuConfigs(rawPHUConfigData),
        });
    }
    return JavelinData;
}());
exports.JavelinData = JavelinData;
/**************************************** METADATA BINDING ****************************************/
/**
 * Bind metadata from DataInterface spit out by the transpiler
 */
Reflect.defineProperty(JavelinData, '$metadata$', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: javelin_exports_1.javelin.data.DataInterface.$metadata$,
});
// Map CVC tradename concept data to Javelin structure
var cvcTradenameConceptsMap = function (cvc) {
    var parentConcept = cvcGenerics.find(function (i) { return i.conceptId === cvc.genericParentConceptId; });
    return {
        DIN: cvc.din,
        gtins: [],
        manufacturer: '',
        conceptId: cvc.canimmId,
        displayTerms: cvc.displayTerm,
        abbreviations: cvc.abbreviation,
        parentId: parentConcept.canimmId,
        picklistPrevalence: cvc.canimmPrevalence,
        preferredTerms: cvc.publicPicklistTerm,
        snomedCTId: cvc.conceptId,
        deleted: false,
        dateCreated: null,
        dateUpdated: cvc.lastUpdated,
    };
};
// Map CVC generic concept data to Javelin structure
var cvcGenericConceptMap = function (cvc) {
    var antigens = cvcAntigens.filter(function (a) { return a.agentConceptId === cvc.conceptId; });
    var diseaseIds = antigens.map(function (a) {
        var disease = cvcDiseases.find(function (d) { return d.conceptId === a.diseaseConceptId; });
        return disease.canimmId;
    });
    return {
        conceptId: cvc.canimmId,
        snomedCTId: cvc.conceptId,
        showInPickList: true,
        diseaseDescriptions: { en: '', fr: '' },
        preferredTerms: cvc.publicPicklistTerm,
        displayTerms: cvc.publicPicklistTerm,
        abbreviations: cvc.abbreviation,
        diseaseConceptIds: diseaseIds,
        color: '#FDB640',
        deleted: false,
        dateCreated: null,
        dateUpdated: cvc.lastUpdated,
    };
};
// Map CVC disease concept data to Javelin structure
var cvcDiseaseConceptMap = function (cvc) {
    return {
        conceptId: cvc.canimmId,
        snomedCTId: cvc.conceptId,
        preferredTerms: cvc.displayTerm,
        deleted: false,
        dateCreated: null,
        dateUpdated: cvc.lastUpdated,
    };
};
//# sourceMappingURL=javelin-data.js.map